import React from 'react';
import { Link } from 'react-router-dom';

const INLINE_MARKUP =
  /\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|contact@long-agi\.com|https:\/\/(?:www\.)?long-agi\.com|https:\/\/(?:www\.)?gressio\.ai|https:\/\/(?:www\.)?translide\.co|gressio\.ai|translide\.co/g;

const EXTERNAL_LINKS = {
  'gressio.ai': 'https://gressio.ai',
  'translide.co': 'https://translide.co',
};

function resolveLinkHref(href) {
  if (href.startsWith('privacy')) {
    const hashIndex = href.indexOf('#');
    if (hashIndex >= 0) {
      return `/privacy${href.slice(hashIndex)}`;
    }
    return '/privacy';
  }

  if (href.startsWith('terms')) {
    const hashIndex = href.indexOf('#');
    if (hashIndex >= 0) {
      return `/terms${href.slice(hashIndex)}`;
    }
    return '/terms';
  }

  return href;
}

function formatLegalBody(text) {
  const parts = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(INLINE_MARKUP)) {
    const index = match.index ?? 0;

    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    if (match[1] && match[2]) {
      const label = match[1];
      const href = resolveLinkHref(match[2]);

      if (href.startsWith('/')) {
        parts.push(
          <Link key={key++} to={href} className="legal-link">
            {label}
          </Link>
        );
      } else {
        parts.push(
          <a
            key={key++}
            href={href}
            className="legal-link"
            {...(href.startsWith('http')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {label}
          </a>
        );
      }
    } else if (match[3]) {
      parts.push(
        <code key={key++} className="legal-code">
          {match[3]}
        </code>
      );
    } else if (match[0] === 'contact@long-agi.com') {
      parts.push(
        <a key={key++} href="mailto:contact@long-agi.com" className="legal-link">
          contact@long-agi.com
        </a>
      );
    } else if (EXTERNAL_LINKS[match[0]]) {
      parts.push(
        <a
          key={key++}
          href={EXTERNAL_LINKS[match[0]]}
          className="legal-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[0]}
        </a>
      );
    } else if (match[0].startsWith('https://')) {
      parts.push(
        <a
          key={key++}
          href={match[0]}
          className="legal-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[0]}
        </a>
      );
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function LegalDocument({
  title,
  effective,
  lastUpdated,
  intro,
  tocLabel,
  backLabel,
  sections,
}) {
  return (
    <article className="legal-document">
      <header className="legal-header">
        <h1>{title}</h1>
        <div className="legal-dates">
          <p>{effective}</p>
          <p>{lastUpdated}</p>
        </div>
        <p className="legal-intro">{formatLegalBody(intro)}</p>
      </header>

      <nav aria-label={tocLabel} className="legal-toc">
        <h2>{tocLabel}</h2>
        <ol>
          {sections.map((section, i) => {
            const sectionNum = i + 1;
            const showSubsectionNumbers = section.subsections.length > 1;

            return (
              <li key={section.id}>
                <a href={`#${section.id}`} className="legal-link">
                  {sectionNum}. {section.title}
                </a>
                {showSubsectionNumbers && (
                  <ol className="legal-toc-sub">
                    {section.subsections.map((subsection, j) => (
                      <li key={subsection.id}>
                        <a href={`#${subsection.id}`} className="legal-toc-sub-link">
                          {sectionNum}.{j + 1}
                          {subsection.title ? ` ${subsection.title}` : ''}
                        </a>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="legal-sections">
        {sections.map((section, i) => {
          const sectionNum = i + 1;
          const showSubsectionNumbers = section.subsections.length > 1;

          return (
            <section key={section.id} id={section.id} className="legal-section">
              <h2>
                <a href={`#${section.id}`} className="legal-heading-link">
                  {sectionNum}. {section.title}
                </a>
              </h2>
              <div className="legal-subsections">
                {section.subsections.map((subsection, j) => {
                  const subsectionNum = `${sectionNum}.${j + 1}`;
                  const showSubsectionHeading =
                    showSubsectionNumbers && subsection.title;

                  return (
                    <div key={subsection.id} id={subsection.id} className="legal-subsection">
                      {showSubsectionHeading ? (
                        <h3>
                          <a href={`#${subsection.id}`} className="legal-heading-link">
                            <span className="legal-subsection-num">{subsectionNum}</span>
                            {subsection.title}
                          </a>
                        </h3>
                      ) : null}
                      <p className={showSubsectionHeading ? 'legal-subsection-body' : ''}>
                        {formatLegalBody(subsection.body)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className="legal-back">
        <Link to="/" className="legal-link">
          {backLabel}
        </Link>
      </div>
    </article>
  );
}

export default LegalDocument;
